import React from 'react'
import { api } from '../api/client'
import type { SuggestionVerdict, VerdictReason } from '../api/client'
import { Icon } from '../ui/Icon'
import './Verdict.css'

const REASONS: { reason: VerdictReason; label: string }[] = [
  // Short enough that all three sit on one line on a phone. "Not new" cannot
  // be read as "I knew it was right"; "Wrong figures" cannot be read as wrong
  // phone numbers on a rebooking suggestion.
  { reason: 'already_knew', label: 'Not new' },
  { reason: 'not_my_business', label: 'Not for me' },
  { reason: 'wrong_numbers', label: 'Wrong figures' },
]

interface Props {
  itemKey?: string
  given?: SuggestionVerdict | null
  // What shares the row, at its left: the Coach's heading, or an action's
  // worth. The thumbs sit at the right of the same line, so asking costs
  // no extra line until she answers thumbs down.
  lead?: React.ReactNode
  // Offer the reasons after a thumbs down. The Coach's summary of the month
  // has none that fit, and no rule reads its verdict, so it records the tap
  // alone.
  reasons?: boolean
}

// Verdict asks whether one Coach line helped: a caption "Helpful?" and two
// small thumbs, quiet until touched (owner, 2026-10-18: no big buttons, no
// pill). Thumbs down is recorded the moment she taps it, so nothing is lost
// when no reason fits; it then lights and offers three fixed reasons as small
// chips on the next line (no stars, no free text, no "Other"), and a chip
// adds its reason to the same verdict. Tapping thumbs down again closes them.
// Without a key (an older API) there is nothing to answer against, and once
// she has answered it never asks again; the lead still shows either way.
export default function Verdict({ itemKey, given, lead, reasons = true }: Props) {
  const [step, setStep] = React.useState<'ask' | 'reasons' | 'thanks'>('ask')
  if (!itemKey || given) return lead ? <div className="verdict-row">{lead}</div> : null

  // A lost verdict costs one data point, not her trust: nothing waits on it
  // and nothing shows a failure.
  const report = (verdict: SuggestionVerdict, reason?: VerdictReason) => {
    api.reportVerdict(itemKey, verdict, reason).catch(() => {})
  }
  const thumbsDown = () => {
    if (step === 'reasons') {
      setStep('thanks')
      return
    }
    report('not_helpful')
    setStep(reasons ? 'reasons' : 'thanks')
  }

  return (
    <div className="verdict-row">
      {lead}
      {step === 'thanks' ? (
        <span className="verdict verdict__thanks" role="status">
          <Icon name="check" size={14} />
          Thanks
        </span>
      ) : (
        <div className="verdict" role="group" aria-label="Was this helpful?">
          <span className="verdict__label" aria-hidden="true">Helpful?</span>
          <button type="button" className="verdict__thumb" aria-label="Helpful" onClick={() => { report('helpful'); setStep('thanks') }}>
            <Icon name="thumb-up" size={16} />
          </button>
          <button
            type="button"
            className="verdict__thumb"
            aria-label="Not really"
            aria-pressed={step === 'reasons'}
            onClick={thumbsDown}
          >
            <Icon name="thumb-down" size={16} />
          </button>
        </div>
      )}
      {step === 'reasons' && (
        <div className="verdict__reasons" role="group" aria-label="What was off?">
          <span className="verdict__why" aria-hidden="true">What was off?</span>
          <span className="verdict__chips">
            {REASONS.map(({ reason, label }) => (
              <button key={reason} type="button" className="verdict__reason" onClick={() => { report('not_helpful', reason); setStep('thanks') }}>
                {label}
              </button>
            ))}
          </span>
        </div>
      )}
    </div>
  )
}
